import { ComponentRef, Injectable, Injector, Signal, TemplateRef, Type, computed, inject, signal, DOCUMENT } from '@angular/core';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { FLOATING_PANEL_DATA } from './floating-panel-data.token';
import { FLOATING_PANEL_REF } from './floating-panel-ref.token';
import { FLOATING_PANEL_HEADER_ACTIONS, FloatingPanelHeaderAction } from './floating-panel-header-actions.token';
import { FLOATING_PANEL_TOPBAR_LEFT } from './floating-panel-topbar-content.token';
import {
  FloatingPanelDockTab,
  FloatingPanelDockZone,
  FloatingPanelLockContext,
  FloatingPanelLockTarget,
  FloatingPanelOptions,
  FloatingPanelPlacementHint,
  FloatingPanelPresentation,
  FloatingPanelRef,
  FloatingPanelSmartStartPosition,
  FloatingPanelStartPosition,
  FloatingPanelState,
  FloatingPanelUndockPlacement,
} from './floating-panel.types';
import { FloatingPanelHostComponent } from './floating-panel-host.component';
import { ViewportService } from '@core/services/viewport.service';

/**
 * Schatting van de panel-afmetingen voor de initiële smart-placement.
 * Wordt gebruikt om auto-flip en viewport-clamp te berekenen vóór het
 * panel in de DOM zit (zie {@link dialog-placement.md} §7.2 — alternatief
 * via constante). Komt overeen met de huidige gate-info "requested"-variant;
 * een variant-aware oplossing volgt zodra de grote variant arriveert
 * (sectie 10 van die spec).
 */
const DEFAULT_PANEL_SIZE = { width: 300, height: 320 } as const;

/** Minimumafstand tussen panel-rand en viewport-rand. */
const VIEWPORT_MARGIN_PX = 8;

/**
 * Ruimere ademruimte aan de **onderrand** van de viewport. Bewust groter
 * dan {@link VIEWPORT_MARGIN_PX} zodat poort-detail-dialogs die onderaan de
 * chart of planet-grid geopend worden nooit pal tegen de onderkant van de
 * pagina kleven — er blijft altijd minstens deze marge zichtbaar. Geldt
 * enkel voor de `maxY`-grens (placement-bounds + post-render nudge); de
 * overige randen blijven {@link VIEWPORT_MARGIN_PX} gebruiken.
 */
const VIEWPORT_BOTTOM_MARGIN_PX = 20;

/** Offset tussen het preferred-punt (cursor / anchor) en de panel-rand. */
const PANEL_CURSOR_OFFSET_PX = 12;

/**
 * Maximaal aanvaarde overlap-ratio tussen een kandidaat-positie en een
 * reeds geopend floating panel, vóór er naar een betere kandidaat gezocht
 * wordt. Bewust strak gehouden (0.25) zodat opeenvolgende panels visueel
 * duidelijk gescheiden landen — zie `docs/dialog-placement.md` §4. Bij
 * overflow (geen enkele kandidaat haalt de drempel) valt het algoritme
 * terug op de minst-overlappende kandidaat.
 */
const OVERLAP_TOLERANCE_RATIO = 0.25;

/**
 * Spiral-zoekstap: afstanden (in pixels) van het preferred-punt waarop
 * kandidaat-posities worden getest. Het algoritme probeert eerst de
 * preferred-positie (afstand 0), dan ringen op deze afstanden. Vervangt
 * de eerdere lineaire diagonal-nudge die te weinig dekking gaf.
 */
const SEARCH_RADII_PX: readonly number[] = [0, 48, 96, 160, 224, 288, 360, 440];

/**
 * Soft-cap op het aantal gelijktijdig **floating** panels dat via
 * automatisch openen (klik op poort / planet-blok / etc.) toegelaten wordt
 * wanneer er geen per-call override (`maxFloatingPanels`) is meegegeven.
 * Wordt het plafond bereikt → het volgende auto-open landt rechtstreeks
 * gedockt (mits `dockable` en er een dock-target bestaat). Zie
 * `docs/dialog-placement.md` §11 + §12.1.
 *
 * Belangrijk: deze drempel geldt **uitsluitend voor auto-open**. Wanneer
 * de gebruiker zelf een gedockt paneel undockt mag het plafond
 * overschreden worden — undocken is een bewuste keuze.
 */
const DEFAULT_MAX_FLOATING_PANELS = 10;

/**
 * Wanneer de spiral-search geen kandidaat vindt onder deze overlap-ratio
 * met bestaande panels, beslissen we dat er **geen redelijke plek meer is**
 * en triggert de auto-dock (mits dockable + dock-target). Dit is de
 * tweede, ruimte-gebaseerde voorwaarde die naast de soft-cap geldt
 * (`docs/dialog-placement.md` §11.1 + §B).
 */
const AUTO_DOCK_OVERLAP_THRESHOLD = 0.5;

/**
 * Extra buffer rond de dock-zone-rect zodat panels niet pal tegen de rand
 * van het docking-paneel landen. Houdt visueel ademruimte tussen floating
 * en docked content.
 */
const DOCK_ZONE_BUFFER_PX = 12;

/** Ruimte tussen een `'beside-dock'`-paneel en de zichtbare dock-rand. */
const BESIDE_DOCK_GAP_PX = 8;

/**
 * Registratie van één gemounte dock-host. `element` is de DOM-root van de
 * dock-zone (voor placement-uitsluiting / drag-to-dock detectie); `zones`
 * is de capability-set van dock-zone-groottes die de host kan tonen;
 * `hidden` meldt een host die gemount maar onzichtbaar is (ingeklapt).
 */
interface DockTargetRegistration {
  readonly element: HTMLElement | null;
  readonly zones: readonly FloatingPanelDockZone[];
  readonly hidden: Signal<boolean>;
}

interface InternalFloatingPanelEntry<TData> {
  readonly key: string;
  readonly state: ReturnType<typeof signal<FloatingPanelState>>;
  readonly data: ReturnType<typeof signal<TData>>;
  readonly dataReadonly: Signal<TData>;
  readonly stateReadonly: Signal<FloatingPanelState>;
  readonly component: Type<unknown>;
  readonly closable: boolean;
  readonly dockable: boolean;
  /**
   * Presentatievorm, **op open-moment** beslist via `ViewportService.isPhone()`
   * en daarna vast voor de levensduur van de entry (geen live-migratie bij
   * resize). `'sheet'` = phone bottom-sheet: bottom-anchored overlay, volle
   * breedte, geen drag, geen dock, maximaal één tegelijk.
   */
  readonly presentation: FloatingPanelPresentation;
  readonly dockTab: FloatingPanelDockTab | undefined;
  /** Reactieve dock-tab-label (default `dockTab.label`; live bijwerkbaar). */
  readonly dockTabLabel: ReturnType<typeof signal<string>>;
  readonly maxFloatingPanels: number;
  /** Door de aanroeper gevraagde dock-zone (default `'small'`). */
  readonly requestedDockZone: FloatingPanelDockZone;
  /** Effectieve dock-zone na degradatie t.o.v. de geregistreerde hosts. */
  readonly effectiveDockZone: ReturnType<typeof signal<FloatingPanelDockZone>>;
  overlayRef: OverlayRef | null;
  hostComponentRef: ComponentRef<FloatingPanelHostComponent> | null;
  /**
   * Observeert de gemonteerde panel-host op groei ná de initiële placement
   * (async content zoals gate-info-documenten): elke maatwijziging her-klemt
   * de positie zodat de onderrand-ademruimte gegarandeerd blijft.
   */
  hostResizeObserver: ResizeObserver | null;
  startPosition: FloatingPanelStartPosition;
  /**
   * Laatst bekende viewport-positie van de panel-host op het moment dat hij
   * in floating-modus stond — gebruikt om bij undock zonder expliciete `at`
   * positie de oorspronkelijke plek te herstellen ipv terug naar het midden
   * te springen.
   */
  lastFloatingPosition: { x: number; y: number } | null;
  /** Plaatsing bij een eerste undock (zie {@link FloatingPanelOptions.undockPlacement}). */
  readonly undockPlacement: FloatingPanelUndockPlacement;
  /**
   * Writable signaal voor content-specifieke icoon-knoppen bovenaan rechts
   * (links van de dock- en sluit-knoppen). De inhoud (of zijn wrapper) update
   * dit signaal en de {@link FloatingPanelHostComponent} leest het via de
   * content-injector.
   */
  readonly headerActions: ReturnType<typeof signal<readonly FloatingPanelHeaderAction[]>>;
  /**
   * Writable signaal voor een TemplateRef die links bovenaan in de topbar
   * gerenderd wordt (gelijke hoogte als de pin/close-iconen rechts). Wordt
   * door de content gebruikt om bv. circuit-info te tonen zonder de gewone
   * heading-tekst breedte af te nemen.
   */
  readonly topbarLeft: ReturnType<typeof signal<TemplateRef<unknown> | null>>;
  /** Er gold een lock-context voor deze key bij het openen (zie {@link FloatingPanelLockContext}). */
  readonly lockable: boolean;
  /** Context waarop het paneel vergrendeld is; `null` = volgt de actieve context. */
  readonly lockedTo: ReturnType<typeof signal<FloatingPanelLockTarget | null>>;
  readonly contentKey: Signal<string | null>;
  publicRef: FloatingPanelRef<unknown, TData>;
}

/**
 * Scheidingsteken tussen de basiskey en het volgnummer van een extra volgend
 * paneel naast een vergrendeld paneel (`gate-info:34~2`).
 */
const LOCK_SIBLING_SEPARATOR = '~';

/** Basiskey zonder volgnummer: `gate-info:34~2` → `gate-info:34`. */
function baseKeyOf(key: string): string {
  const index = key.lastIndexOf(LOCK_SIBLING_SEPARATOR);
  return index > 0 && /^\d+$/.test(key.slice(index + 1)) ? key.slice(0, index) : key;
}

/**
 * Generieke service om niet-modale, draggable, multi-instance floating panels
 * te openen via CDK Overlay. Eén service-instance houdt alle panels bij —
 * zowel floating (in een overlay) als gedockt (in een
 * `DockedPanelsHostComponent`). Dedup gebeurt op `options.key`.
 */
@Injectable({ providedIn: 'root' })
export class FloatingPanelService {
  private readonly overlay = inject(Overlay);
  private readonly injector = inject(Injector);
  private readonly document = inject(DOCUMENT);
  private readonly viewport = inject(ViewportService);

  private readonly entries = signal<readonly InternalFloatingPanelEntry<unknown>[]>([]);
  private readonly lastInteracted: string[] = [];
  private readonly dockTargets = signal<readonly DockTargetRegistration[]>([]);
  private readonly dockTargetAvailable = computed(() => this.dockTargets().length > 0);
  /** Minstens één dock-host die ook zichtbaar is — zie {@link shouldAutoDock}. */
  private readonly visibleDockTargetAvailable = computed(() =>
    this.dockTargets().some(target => !target.hidden()));
  private readonly dockTargetElement = computed<HTMLElement | null>(() => {
    const withElement = this.dockTargets().filter(target => target.element !== null);
    return withElement.length > 0 ? withElement[withElement.length - 1].element : null;
  });
  /** Vereniging van alle dock-zones die de gemounte hosts ondersteunen. */
  private readonly supportedDockZones = computed<ReadonlySet<FloatingPanelDockZone>>(() => {
    const zones = new Set<FloatingPanelDockZone>();
    for (const target of this.dockTargets()) {
      for (const zone of target.zones) zones.add(zone);
    }
    return zones;
  });
  private readonly dragOverDockZone = signal(false);
  /**
   * Open-guard (spec 2026-10-20-chart-share §4.4): zolang gezet beslist hij per
   * {@link openOrFocus} of een paneel mag openen. Weigert hij, dan krijgt de
   * aanroeper een inert ref terug en gebeurt er niets. De gastpagina gebruikt
   * dit om élke info-/paneelklik, hoe diep ook, naar de accountmelding te sturen.
   */
  private openGuard: (() => boolean) | null = null;
  /** Zie {@link setLockContext}; een signal zodat de slotknop-tooltip meebeweegt. */
  private readonly lockContext = signal<FloatingPanelLockContext | null>(null);
  private readonly lockCandidate = computed<FloatingPanelLockTarget | null>(() => this.lockContext()?.current() ?? null);
  /**
   * BELANGRIJK: `equal: () => false` zodat élke focus-markering de dock-host
   * bereikt, óók met dezelfde key kort na elkaar (bv. twee voice-/angst-
   * icoon-kliks naar hetzelfde gedockte paneel binnen het 900ms-pulse-venster
   * van de host). Met default-equality dedupliceerde het signaal de tweede
   * markering: de dock-host activeerde de tab dan niet, het paneel bleef
   * verborgen en het focus-scroll-venster van het paneel verliep ongebruikt
   * (bugfix 2026-09-22, "transit-voice-klik scrolt niet").
   */
  private readonly justAutoDockedKey = signal<string | null>(null, { equal: () => false });
  private escListenerAttached = false;
  private escListener: ((event: KeyboardEvent) => void) | null = null;
  private nextZIndex = 1001;
  /** Teller voor de unieke pane-klasse waarmee een geopende dialoog teruggevonden wordt. */
  private raisedDialogCount = 0;

  /**
   * BELANGRIJK: modale dialogen (MatDialog, dus ook e-dialog en de confirmer)
   * boven de zwevende panelen leggen. Zwevende panelen krijgen bij elke focus
   * een hogere z-index (vanaf 1002) dan de CDK-standaard 1000 van een
   * dialoog-laag; zonder dit viel een dialoog die daarna opent áchter een
   * zwevend paneel (bugfix 2026-10-01: de beeldeditor van een screenshot vanuit
   * het losgemaakte notitievenster). De dialoog krijgt een plek op dezelfde
   * teller, zodat een paneel dat later (vanuit de dialoog) opent er weer boven
   * komt. Niet weghalen: zie de spec "legt een later geopende dialoog boven".
   */
  private readonly raiseDialogsAbovePanels = inject(MatDialog).afterOpened
    .subscribe(dialogRef => this.raiseDialogAbovePanels(dialogRef));

  readonly panels: Signal<readonly FloatingPanelRef<unknown, unknown>[]> = computed(() =>
    this.entries().map(entry => entry.publicRef),
  );

  readonly dockedPanels: Signal<readonly FloatingPanelRef<unknown, unknown>[]> = computed(() =>
    this.panels().filter(panel => panel.state() === 'docked'),
  );

  /**
   * Reactieve lijst van gedockte panels waarvan de **effectieve** dock-zone
   * in `zones` valt. Bedoeld voor dock-hosts die enkel hun eigen zone(s)
   * willen tonen (bv. een smalle host toont enkel `'small'`-panels, een
   * full-host toont `'small'` én `'full'`). De `dockedPanels`-signal (alle
   * gedockte panels, zone-onafhankelijk) blijft bestaan voor de bestaande
   * single-zone host.
   */
  dockedPanelsInZones(zones: readonly FloatingPanelDockZone[]): Signal<readonly FloatingPanelRef<unknown, unknown>[]> {
    const zoneSet = new Set(zones);
    return computed(() => this.dockedPanels().filter(panel => zoneSet.has(panel.dockZone())));
  }

  /**
   * Wordt door {@link DockedPanelsHostComponent} aangeroepen bij mount.
   * Het optionele `element` is de DOM-root van het docking-paneel — wordt
   * gebruikt door {@link resolveSmartPosition} om de dock-zone uit te
   * sluiten als landingsplek (geen auto-placement op de strook) en door
   * de drag-to-dock detectie (`reportDragPointer` / `handleDragEnded`).
   * Wanneer geen element wordt meegegeven valt de dock-zone uitsluiting
   * stil terug op de oude semantiek (dock-target enkel kennen).
   *
   * `options.zones` declareert de dock-zone-groottes die deze host kan
   * tonen (default `['small']`). De service gebruikt de vereniging van alle
   * geregistreerde zones om te beslissen of een `'full'`-paneel zijn
   * gevraagde zone behoudt of degradeert naar `'small'`.
   *
   * `options.hidden` meldt live dat de host gemount maar onzichtbaar is (bv.
   * het ingeklapte rechterpaneel): de auto-dock-heuristiek slaat zo'n host
   * over, zie {@link shouldAutoDock}.
   */
  registerDockTarget(
    element?: HTMLElement,
    options?: { readonly zones?: readonly FloatingPanelDockZone[]; readonly hidden?: Signal<boolean> },
  ): () => void {
    const registration: DockTargetRegistration = {
      element: element ?? null,
      zones: options?.zones ?? ['small'],
      hidden: options?.hidden ?? signal(false),
    };
    this.dockTargets.update(list => [...list, registration]);
    this.reconcileEffectiveDockZones();
    return () => {
      this.dockTargets.update(list => list.filter(target => target !== registration));
      this.reconcileEffectiveDockZones();
    };
  }

  /**
   * Herberekent de effectieve dock-zone van alle gedockte panels nadat de
   * set geregistreerde hosts wijzigde (mount/unmount). Zo degradeert een
   * `'full'`-paneel naar `'small'` zodra de full-host verdwijnt, en herstelt
   * het wanneer een full-host (terug) verschijnt.
   */
  private reconcileEffectiveDockZones(): void {
    for (const entry of this.entries()) {
      entry.effectiveDockZone.set(this.resolveEffectiveDockZone(entry.requestedDockZone));
    }
  }

  /**
   * Lost de gevraagde dock-zone op tegen de capability-set van de gemounte
   * hosts: `'full'` blijft enkel `'full'` als minstens één host die zone
   * ondersteunt — anders `'small'`.
   */
  private resolveEffectiveDockZone(requested: FloatingPanelDockZone): FloatingPanelDockZone {
    if (requested === 'small') return 'small';
    return this.supportedDockZones().has(requested) ? requested : 'small';
  }

  hasDockTarget(): Signal<boolean> {
    return this.dockTargetAvailable;
  }

  /**
   * Reactieve indicator voor de dock-host of de gebruiker momenteel een
   * paneel boven de dock-zone hangt. De host gebruikt dit om een
   * "drop-target" highlight te tonen zodat zichtbaar is dat loslaten op
   * deze plek het paneel zal docken.
   */
  isDragOverDockZone(): Signal<boolean> {
    return this.dragOverDockZone;
  }

  /**
   * Sleutel van het laatst auto-gedockte paneel — wordt door de dock-host
   * gebruikt om een korte pulse-animatie op de overeenkomstige tab te
   * triggeren. De dock-host roept {@link clearJustAutoDocked} aan zodra de
   * animatie verwerkt is zodat hetzelfde key niet blijft pulsen.
   */
  getJustAutoDockedKey(): Signal<string | null> {
    return this.justAutoDockedKey;
  }

  clearJustAutoDocked(): void {
    this.justAutoDockedKey.set(null);
  }

  /**
   * Registreert een open-guard (zie {@link openGuard}); hooguit één tegelijk.
   * Geeft een unregister-functie terug; een latere guard verdringt een eerdere.
   */
  setOpenGuard(guard: () => boolean): () => void {
    this.openGuard = guard;
    return () => {
      if (this.openGuard === guard) this.openGuard = null;
    };
  }

  /**
   * Registreert de lock-context van een pagina wiens info-panelen de actieve
   * context volgen (zie {@link FloatingPanelLockContext}); hooguit één
   * tegelijk. Geeft een unregister-functie terug; een latere context verdringt
   * een eerdere. Al vergrendelde panelen blijven vergrendeld na unregister.
   */
  setLockContext(context: FloatingPanelLockContext): () => void {
    this.lockContext.set(context);
    return () => {
      if (this.lockContext() === context) this.lockContext.set(null);
    };
  }

  /**
   * De key waaronder {@link openOrFocus} een paneel met deze key opent of
   * focust. Zonder lock-context voor de key is dat de key zelf. Anders, binnen
   * de familie van de basiskey (`gate-info:34`, `gate-info:34~2`, …):
   *  1. een volgend (niet vergrendeld) paneel: dat toont de actieve context al;
   *  2. anders een paneel dat op de huidige context vergrendeld is (geen dubbel);
   *  3. anders een nieuwe key: de basiskey of het eerste vrije volgnummer.
   * Zo krijgt een vergrendeld paneel nooit de data van een andere context.
   * Idempotent: een opgeloste key lost op naar zichzelf.
   */
  resolveKey(key: string): string {
    const base = baseKeyOf(key);
    const context = this.lockContext();
    if (!context?.appliesTo(base)) return key;
    const family = this.entries().filter(entry => baseKeyOf(entry.key) === base);
    const following = family.find(entry => entry.lockedTo() === null);
    if (following) return following.key;
    const currentId = context.current()?.id;
    const lockedHere = currentId == null ? undefined : family.find(entry => entry.lockedTo()?.id === currentId);
    if (lockedHere) return lockedHere.key;
    if (!family.some(entry => entry.key === base)) return base;
    let index = 2;
    while (family.some(entry => entry.key === `${base}${LOCK_SIBLING_SEPARATOR}${index}`)) index += 1;
    return `${base}${LOCK_SIBLING_SEPARATOR}${index}`;
  }

  openOrFocus<TComponent, TData>(
    options: FloatingPanelOptions<TComponent, TData>,
  ): FloatingPanelRef<TComponent, TData> {
    if (this.openGuard && !this.openGuard()) {
      return this.createBlockedRef(options);
    }
    this.ensureEscListener();

    const key = this.resolveKey(options.key);
    const existing = this.entries().find(entry => entry.key === key);
    if (existing) {
      this.focusExistingEntry(
        existing as InternalFloatingPanelEntry<TData>,
        options.data,
        options.redockOnReopen ?? false,
      );
      return existing.publicRef as FloatingPanelRef<TComponent, TData>;
    }

    const entry = this.createEntry<TComponent, TData>({ ...options, key });
    if (entry.presentation === 'sheet') {
      // Eén sheet tegelijk op phone: een nieuw paneel vervangt de vorige
      // zwevende paneel(en) i.p.v. te stapelen — er is simpelweg geen ruimte
      // voor meerdere overlays. Desktop/tablet behouden de soft-cap van
      // {@link DEFAULT_MAX_FLOATING_PANELS}. Dedup-by-key blijft gelden
      // (die tak is hierboven al afgehandeld).
      for (const other of [...this.entries()]) {
        if (other.state() === 'floating') other.publicRef.close();
      }
    }
    const openAsDocked = this.resolveInitialDockState(entry, options);
    if (openAsDocked) {
      entry.state.set('docked');
    }
    this.entries.update(list => [...list, entry as InternalFloatingPanelEntry<unknown>]);
    this.markInteracted(entry.key);
    if (!openAsDocked) {
      this.attachToOverlay(entry as InternalFloatingPanelEntry<TData>);
    } else {
      // Signaleer aan de dock-host dat deze tab kort moet "pulsen" zodat
      // de gebruiker — ook met een drukke zijbalk — opmerkt dat het nieuwe
      // paneel daar geland is.
      this.justAutoDockedKey.set(entry.key);
    }
    return entry.publicRef as FloatingPanelRef<TComponent, TData>;
  }

  /**
   * Beslist of een nieuw paneel gedockt of zwevend start: de expliciete
   * `openInitiallyDocked`-vraag wint, daarna een expliciete `bypassAutoDock`
   * (altijd zwevend), en anders beslist de heuristiek van
   * {@link shouldAutoDock}.
   */
  private resolveInitialDockState<TComponent, TData>(
    entry: InternalFloatingPanelEntry<TData>,
    options: FloatingPanelOptions<TComponent, TData>,
  ): boolean {
    // Phone-sheets docken nooit: ook een expliciete `openInitiallyDocked`
    // (bv. de dock-varianten van `gateInfoOpenBehavior`) valt daar terug op
    // de sheet-presentatie — de dock-zijbalk bestaat niet in de mobiele
    // compositie.
    if (entry.presentation === 'sheet') return false;
    if (options.openInitiallyDocked) return entry.dockable && this.dockTargetAvailable();
    if (options.bypassAutoDock) return false;
    return this.shouldAutoDock(entry);
  }

  /**
   * Bepaalt of een nieuw paneel rechtstreeks gedockt geopend moet worden
   * i.p.v. floating. Twee onafhankelijke triggers (`docs/dialog-placement.md`
   * §11.1):
   *  1. **Soft-cap**: aantal open floating panels ≥ entry's `maxFloatingPanels`
   *     (default {@link DEFAULT_MAX_FLOATING_PANELS}).
   *  2. **Geen ruimte meer**: de beste plek die de spiral-search vindt
   *     overlapt nog steeds te zwaar (> {@link AUTO_DOCK_OVERLAP_THRESHOLD})
   *     met bestaande panels + dock-zone. Hierdoor blijven panels floaten
   *     zolang er werkelijk ruimte is, en dockt het systeem pas wanneer
   *     overlap onvermijdelijk wordt.
   *
   * Vereisten in beide gevallen: `dockable !== false` en er is een
   * **zichtbaar** dock-target geregistreerd. Geldt enkel voor auto-open —
   * undock-acties van de gebruiker omzeilen deze methode.
   *
   * BELANGRIJK: een verborgen host (ingeklapt rechterpaneel) telt hier niet
   * mee. Het ingeklapte paneel blijft gemount en geregistreerd; zonder deze
   * check dockte de ruimtedruk-regel bv. de radiale poort-kaart die vanuit een
   * zwevende gate-info geopend werd onzichtbaar weg: de eerste klik leek dood,
   * pas de tweede klik maakte het paneel los (bugfix 2026-10-15). Expliciete
   * dock-vragen (`openInitiallyDocked`) beslissen de aanroepers zelf. Zie de
   * spec "dockt niet automatisch in een ingeklapte dock".
   */
  private shouldAutoDock<TData>(entry: InternalFloatingPanelEntry<TData>): boolean {
    if (!entry.dockable) return false;
    if (!this.visibleDockTargetAvailable()) return false;
    const floatingCount = this.entries().filter(other => other.state() === 'floating').length;
    if (floatingCount >= entry.maxFloatingPanels) return true;
    return this.hasNoRoomForFloating(entry);
  }

  /**
   * Ruimtedruk-check: simuleer de spiral-search voor de gegeven entry's
   * `startPosition` en kijk of de beste kandidaat ondanks alles nog
   * > {@link AUTO_DOCK_OVERLAP_THRESHOLD} overlapt met bestaande panels +
   * dock-zone. Enkel relevant voor smart-startposities (cursor / anchor);
   * voor `'center'` of vaste pixels respecteert het systeem nog steeds de
   * keuze van de aanroeper.
   */
  private hasNoRoomForFloating<TData>(entry: InternalFloatingPanelEntry<TData>): boolean {
    const start = entry.startPosition;
    if (start === 'center') return false;
    if ('x' in start && 'y' in start) return false;
    const existingRects = this.collectFloatingPanelRects(entry.key);
    const obstacles = this.withDockZone(existingRects);
    if (obstacles.length === 0) return false;
    const panelSize = this.estimatePanelSize(existingRects);
    const bounds = this.computePlacementBounds(panelSize);
    const initial = this.computeInitialCandidate(
      start.preferred,
      panelSize,
      this.readViewportSize(),
      start.hint ?? 'prefer-right-of-anchor',
      bounds,
    );
    const best = this.findBestCandidate(
      initial,
      start.preferred,
      panelSize,
      obstacles,
      start.hint ?? 'prefer-right-of-anchor',
      bounds,
    );
    const overlap = this.maxOverlapRatio(best, panelSize, obstacles);
    return overlap > AUTO_DOCK_OVERLAP_THRESHOLD;
  }

  /**
   * Verwerkt de pointer-positie tijdens een drag-operatie op een floating
   * paneel. Wanneer de pointer binnen de dock-zone valt, zetten we de
   * {@link dragOverDockZone}-signal zodat de dock-host een drop-target
   * highlight toont. Bij geen geregistreerd dock-element of niet-dockable
   * paneel doen we niets.
   */
  reportDragPointer<TData>(
    entry: InternalFloatingPanelEntry<TData>,
    pos: { readonly x: number; readonly y: number },
  ): void {
    if (!entry.dockable) return;
    const rect = this.readDockZoneRect();
    if (!rect) {
      if (this.dragOverDockZone()) this.dragOverDockZone.set(false);
      return;
    }
    const inside = this.isInside(pos, rect);
    if (inside !== this.dragOverDockZone()) this.dragOverDockZone.set(inside);
  }

  /**
   * Drag-end handler: indien het paneel boven de dock-zone is losgelaten
   * → dock het automatisch. Anders reset enkel de highlight-signal.
   * Wordt door {@link FloatingPanelHostComponent} aangeroepen via
   * `dragPointerEnded`.
   */
  handleDragEnded<TData>(
    entry: InternalFloatingPanelEntry<TData>,
    pos: { readonly x: number; readonly y: number },
  ): void {
    const wasOver = this.dragOverDockZone();
    this.dragOverDockZone.set(false);
    if (!wasOver) return;
    if (!entry.dockable) return;
    if (!this.dockTargetAvailable()) return;
    if (entry.state() !== 'floating') return;
    entry.publicRef.dock();
  }

  private isInside(
    pos: { readonly x: number; readonly y: number },
    rect: DOMRect,
  ): boolean {
    return pos.x >= rect.left && pos.x <= rect.right
      && pos.y >= rect.top && pos.y <= rect.bottom;
  }

  closeAll(): void {
    [...this.entries()].forEach(entry => entry.publicRef.close());
  }

  /** Inert ref voor een door de open-guard geweigerd paneel: alles no-op, state 'closed'. */
  private createBlockedRef<TComponent, TData>(
    options: FloatingPanelOptions<TComponent, TData>,
  ): FloatingPanelRef<TComponent, TData> {
    const noop = (): void => undefined;
    return {
      key: options.key,
      state: signal<FloatingPanelState>('closed').asReadonly(),
      data: signal(options.data).asReadonly(),
      component: options.component,
      dockable: options.dockable ?? false,
      closable: options.closable ?? true,
      presentation: 'floating',
      dockTab: options.dockTab,
      dockZone: signal<FloatingPanelDockZone>(options.dockZone ?? 'small').asReadonly(),
      dockTabLabel: signal(options.dockTab?.label ?? '').asReadonly(),
      lockable: false,
      lockedTo: signal<FloatingPanelLockTarget | null>(null).asReadonly(),
      lockCandidate: signal<FloatingPanelLockTarget | null>(null).asReadonly(),
      toggleLock: noop,
      contentKey: signal<string | null>(null).asReadonly(),
      updateData: noop,
      setDockTabLabel: noop,
      replaceData: noop,
      bringToFront: noop,
      recenter: noop,
      nudgeIntoView: noop,
      getPosition: () => null,
      moveTo: noop,
      dock: noop,
      undock: noop,
      close: noop,
    };
  }

  closeForKey(key: string): void {
    const entry = this.entries().find(panelEntry => panelEntry.key === key);
    entry?.publicRef.close();
  }

  /**
   * Slotknop: een volgend paneel vergrendelen op de huidige context, of een
   * vergrendeld paneel weer laten volgen. Na ontgrendelen synct de pagina het
   * paneel meteen met de actieve context (`unlocked`-callback), anders bleef
   * het de oude context tonen tot de volgende tabwissel.
   */
  private toggleEntryLock<TData>(entry: InternalFloatingPanelEntry<TData>): void {
    if (!entry.lockable) return;
    if (entry.lockedTo() !== null) {
      entry.lockedTo.set(null);
      this.lockContext()?.unlocked?.(entry.publicRef as FloatingPanelRef);
      return;
    }
    const target = this.lockContext()?.current() ?? null;
    if (target) entry.lockedTo.set(target);
  }

  private focusExistingEntry<TData>(
    entry: InternalFloatingPanelEntry<TData>,
    data: TData,
    redockOnReopen: boolean,
  ): void {
    entry.data.set(data);
    if (entry.state() === 'docked') {
      // Enkel als er op deze pagina ook een dock gemount is: een paneel dat op
      // de chart-pagina gedockt werd, blijft in deze (app-brede) service
      // "docked" na navigatie naar een pagina zonder dock (bv. een cheat
      // sheet). De tab naar voren halen deed daar niets zichtbaars — de klik
      // leek dood (bugfix 2026-09-25, love-icoon in de mini cheat sheet).
      if (redockOnReopen && this.dockTargetAvailable()) {
        // "Full docked"-modi: een her-klik op een reeds gedockte poort mag het
        // paneel niet losmaken. Breng enkel de bestaande gedockte tab naar
        // voren (de pulse-signal activeert + highlight de tab in de dock-host).
        this.markInteracted(entry.key);
        this.justAutoDockedKey.set(entry.key);
        return;
      }
      // Heropen als floating zodat een klik op hetzelfde item in de planet-grid
      // altijd de dialog terug op de voorgrond brengt — anders zou de gebruiker
      // moeten weten dat het paneel verstopt zit in de docked-tabs (of, zonder
      // dock op de pagina, nergens te zien is).
      this.undockEntry(entry);
      return;
    }
    if (entry.state() === 'floating') {
      // Sheet-entries (phone) redocken nooit — enkel naar voren brengen.
      if (redockOnReopen && entry.presentation !== 'sheet' && entry.dockable && this.dockTargetAvailable()) {
        // "Full docked"-modi: een her-klik op een reeds open maar (handmatig)
        // ontdockte poort dockt ze terug. Ze blijft een handmatig item: de
        // aanroeper trackt ze niet als auto-docked, dus latere klikken sluiten
        // ze niet automatisch. De pulse-signal activeert + highlight de tab.
        this.dockEntry(entry);
        this.markInteracted(entry.key);
        this.justAutoDockedKey.set(entry.key);
        return;
      }
      this.bringToFront(entry);
    }
    this.markInteracted(entry.key);
  }

  private createEntry<TComponent, TData>(
    options: FloatingPanelOptions<TComponent, TData>,
  ): InternalFloatingPanelEntry<TData> {
    const stateSignal = signal<FloatingPanelState>('floating');
    const dataSignal = signal<TData>(options.data);
    const startPosition = options.startPosition ?? 'center';
    const closable = options.closable ?? true;
    const dockable = options.dockable ?? true;
    const requestedDockZone: FloatingPanelDockZone = options.dockZone ?? 'small';
    // Presentatie wordt éénmalig op open-moment beslist (docs/MOBIEL.md):
    // op phone-breedte een bottom-sheet, elders het bestaande floating gedrag.
    const presentation: FloatingPanelPresentation = this.viewport.isPhone() ? 'sheet' : 'floating';

    const entry: InternalFloatingPanelEntry<TData> = {
      key: options.key,
      state: stateSignal,
      data: dataSignal,
      stateReadonly: stateSignal.asReadonly(),
      dataReadonly: dataSignal.asReadonly(),
      component: options.component as Type<unknown>,
      closable,
      dockable,
      presentation,
      dockTab: options.dockTab,
      dockTabLabel: signal<string>(options.dockTab?.label ?? ''),
      maxFloatingPanels: options.maxFloatingPanels ?? DEFAULT_MAX_FLOATING_PANELS,
      requestedDockZone,
      effectiveDockZone: signal<FloatingPanelDockZone>(this.resolveEffectiveDockZone(requestedDockZone)),
      overlayRef: null,
      hostComponentRef: null,
      hostResizeObserver: null,
      startPosition,
      lastFloatingPosition: null,
      undockPlacement: options.undockPlacement ?? 'center',
      headerActions: signal<readonly FloatingPanelHeaderAction[]>([]),
      topbarLeft: signal<TemplateRef<unknown> | null>(null),
      lockable: this.lockContext()?.appliesTo(baseKeyOf(options.key)) ?? false,
      lockedTo: signal<FloatingPanelLockTarget | null>(null),
      contentKey: computed(() => options.contentKey?.(dataSignal()) ?? null),
      publicRef: null as unknown as FloatingPanelRef<unknown, TData>,
    };

    entry.publicRef = this.buildPublicRef<TComponent, TData>(entry);
    return entry;
  }

  private buildPublicRef<TComponent, TData>(
    entry: InternalFloatingPanelEntry<TData>,
  ): FloatingPanelRef<TComponent, TData> {
    return {
      key: entry.key,
      state: entry.stateReadonly,
      data: entry.dataReadonly,
      component: entry.component as Type<TComponent>,
      dockable: entry.dockable,
      closable: entry.closable,
      presentation: entry.presentation,
      dockTab: entry.dockTab,
      // Een vergrendeld paneel draagt de naam van zijn context in de tab
      // ("34 · Anke"): zo blijft zichtbaar dat het niet meer meewisselt.
      dockTabLabel: computed(() => {
        const label = entry.dockTabLabel();
        const lockedTo = entry.lockedTo();
        return lockedTo ? `${label} · ${lockedTo.label}` : label;
      }),
      dockZone: entry.effectiveDockZone.asReadonly(),
      lockable: entry.lockable,
      lockedTo: entry.lockedTo.asReadonly(),
      lockCandidate: this.lockCandidate,
      toggleLock: () => this.toggleEntryLock(entry),
      contentKey: entry.contentKey,
      setDockTabLabel: (label: string) => entry.dockTabLabel.set(label),
      updateData: (newData: TData) => {
        entry.data.set(newData);
        if (entry.state() === 'floating') {
          this.bringToFront(entry);
        }
        this.markInteracted(entry.key);
      },
      replaceData: (newData: TData) => {
        entry.data.set(newData);
      },
      bringToFront: () => {
        if (entry.state() === 'floating') {
          this.bringToFront(entry);
        }
        this.markInteracted(entry.key);
      },
      recenter: () => this.recenterEntry(entry),
      nudgeIntoView: () => this.nudgeEntryIntoView(entry),
      getPosition: () => this.readEntryPosition(entry),
      moveTo: (position: { x: number; y: number }) => this.moveEntryTo(entry, position),
      dock: () => this.dockEntry(entry),
      undock: (at?: { x: number; y: number }) => this.undockEntry(entry, at),
      close: () => this.closeEntry(entry),
    };
  }

  private attachToOverlay<TData>(entry: InternalFloatingPanelEntry<TData>): void {
    const sheet = entry.presentation === 'sheet';
    // Sheet-modus (phone): vast onderaan de viewport, volle breedte — de
    // cursor-anchored spiral-placement is daar betekenisloos. Floating-modus
    // behoudt het bestaande smart-placement gedrag.
    let resolved: { x: number; y: number } | null = null;
    if (!sheet) {
      const existingRects = this.collectFloatingPanelRects(entry.key);
      const obstacles = this.withDockZone(existingRects);
      resolved = this.resolveStartPosition(entry.startPosition, existingRects, obstacles);
    }
    const positionStrategy = sheet
      ? this.overlay.position().global().left('0').bottom('0')
      : resolved
        ? this.overlay.position().global().left(`${resolved.x}px`).top(`${resolved.y}px`)
        : this.overlay.position().global().centerHorizontally().centerVertically();

    const overlayRef = this.overlay.create({
      hasBackdrop: false,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      positionStrategy,
      panelClass: sheet ? ['floating-panel-pane', 'floating-panel-pane--sheet'] : 'floating-panel-pane',
      width: sheet ? '100vw' : undefined,
    });

    const contentInjector = Injector.create({
      parent: this.injector,
      providers: [
        { provide: FLOATING_PANEL_DATA, useValue: entry.dataReadonly },
        { provide: FLOATING_PANEL_REF, useValue: entry.publicRef },
        { provide: FLOATING_PANEL_HEADER_ACTIONS, useValue: entry.headerActions },
        { provide: FLOATING_PANEL_TOPBAR_LEFT, useValue: entry.topbarLeft },
      ],
    });

    const portal = new ComponentPortal(FloatingPanelHostComponent, null, this.injector);
    const hostRef = overlayRef.attach(portal);
    hostRef.setInput('componentType', entry.component);
    hostRef.setInput('contentInjector', contentInjector);
    hostRef.setInput('closable', entry.closable);
    hostRef.setInput('dockable', entry.dockable);
    hostRef.setInput('dockTab', entry.dockTab);
    // Sheets kennen geen dock-affordance: de dock-knop verdwijnt en de host
    // schakelt zijn CdkDrag + drag-strook uit.
    hostRef.setInput('hasDockTarget', sheet ? false : this.dockTargetAvailable());
    hostRef.setInput('sheetMode', sheet);
    hostRef.instance.closeRequested.subscribe(() => entry.publicRef.close());
    hostRef.instance.dockRequested.subscribe(() => entry.publicRef.dock());
    hostRef.instance.bringToFront.subscribe(() => entry.publicRef.bringToFront());
    hostRef.instance.dragPointerMoved.subscribe(pos => this.reportDragPointer(entry, pos));
    hostRef.instance.dragPointerEnded.subscribe(pos => this.handleDragEnded(entry, pos));

    entry.overlayRef = overlayRef;
    entry.hostComponentRef = hostRef;
    this.bringToFront(entry);

    if (resolved) {
      this.nudgeIntoViewportAfterRender(entry, resolved);
    }

    // Panelen groeien vaak pas ná de mount naar hun echte hoogte (async
    // geladen content, focus-rand, small→full). De eenmalige post-render
    // nudge mist die groei, waardoor de onderrand tot een haarlijn tegen (of
    // nét onder) de viewport-rand kon eindigen. Elke maatwijziging klemt
    // daarom opnieuw binnen de viewport-marges. Sheets zijn bottom-anchored
    // en klemmen via CSS (max-height 80vh) — geen observer nodig.
    if (!sheet && typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(() => {
        if (entry.state() === 'floating') {
          this.nudgeEntryIntoView(entry);
        }
      });
      observer.observe(hostRef.location.nativeElement as HTMLElement);
      entry.hostResizeObserver = observer;
    }
  }

  /**
   * Na het effectief mounten van het paneel meten we de **werkelijke**
   * afmetingen op en corrigeren we de overlay-positie wanneer het paneel
   * onder of rechts buiten de viewport zou eindigen. Nodig omdat de
   * pre-mount placement vertrekt van {@link DEFAULT_PANEL_SIZE} (of het
   * grootste reeds open paneel) — voor varianten die afwijken van die
   * schatting kan de geklemde positie alsnog te laag uitvallen, met name
   * bij klikken op poorten helemaal onderaan de chart.
   */
  private nudgeIntoViewportAfterRender<TData>(
    entry: InternalFloatingPanelEntry<TData>,
    initialPos: { readonly x: number; readonly y: number },
  ): void {
    const view = this.document.defaultView;
    if (!view) return;
    view.requestAnimationFrame(() => {
      const overlayRef = entry.overlayRef;
      const hostElement = entry.hostComponentRef?.location.nativeElement as HTMLElement | undefined;
      if (!overlayRef || !hostElement) return;
      const rect = hostElement.getBoundingClientRect();
      if (rect.width === 0 && rect.height === 0) return;
      const viewport = this.readViewportSize();
      let x = initialPos.x;
      let y = initialPos.y;
      let changed = false;
      const maxY = viewport.height - rect.height - VIEWPORT_BOTTOM_MARGIN_PX;
      if (y > maxY) {
        y = Math.max(VIEWPORT_MARGIN_PX, maxY);
        changed = true;
      }
      const maxX = viewport.width - rect.width - VIEWPORT_MARGIN_PX;
      if (x > maxX) {
        x = Math.max(VIEWPORT_MARGIN_PX, maxX);
        changed = true;
      }
      if (!changed) return;
      const newStrategy = this.overlay.position().global().left(`${x}px`).top(`${y}px`);
      overlayRef.updatePositionStrategy(newStrategy);
    });
  }

  /**
   * Hercentreert een floating paneel in de viewport en reset de drag-translate.
   * Bedoeld voor inhoud die plots groeit (gate-info small→full): zo valt het
   * vergrote paneel niet deels buiten beeld. Omdat de inhoudswissel pas in de
   * volgende change-detection in de DOM landt, meten we de **werkelijke**
   * afmetingen in een `requestAnimationFrame` en clampen we de top zodat de
   * drag-strook altijd zichtbaar blijft (ook bij panels hoger dan de viewport).
   */
  private recenterEntry<TData>(entry: InternalFloatingPanelEntry<TData>): void {
    if (entry.state() !== 'floating') return;
    // Sheets liggen vast onderaan de viewport — herpositioneren is een no-op.
    if (entry.presentation === 'sheet') return;
    const overlayRef = entry.overlayRef;
    if (!overlayRef) return;
    entry.hostComponentRef?.instance.resetDragPosition();
    this.bringToFront(entry);
    const view = this.document.defaultView;
    if (!view) return;
    view.requestAnimationFrame(() => {
      const hostElement = entry.hostComponentRef?.location.nativeElement as HTMLElement | undefined;
      if (!entry.overlayRef || !hostElement) return;
      const rect = hostElement.getBoundingClientRect();
      if (rect.width === 0 && rect.height === 0) return;
      const viewport = this.readViewportSize();
      const x = Math.max(VIEWPORT_MARGIN_PX, (viewport.width - rect.width) / 2);
      const y = Math.max(VIEWPORT_MARGIN_PX, (viewport.height - rect.height) / 2);
      const strategy = this.overlay.position().global().left(`${x}px`).top(`${y}px`);
      entry.overlayRef.updatePositionStrategy(strategy);
      entry.startPosition = { x, y };
      entry.lastFloatingPosition = { x, y };
    });
  }

  /**
   * Houdt het paneel op zijn huidige plek maar klemt het minimaal terug in
   * beeld wanneer de inhoud groter werd (bv. kanaal→poort-wissel) en onder-
   * of rechts buiten de viewport zou vallen. Anders dan {@link recenterEntry}
   * wordt de horizontale positie behouden en de top enkel omhoog geschoven wat
   * strikt nodig is — zo blijft een handmatig weggesleept paneel in dezelfde
   * zone. Meet ná de render (rAF) omdat de nieuwe inhoud pas in de volgende
   * change-detection in de DOM landt.
   */
  private nudgeEntryIntoView<TData>(entry: InternalFloatingPanelEntry<TData>): void {
    if (entry.state() !== 'floating') return;
    // Sheets liggen vast onderaan de viewport — herpositioneren is een no-op.
    if (entry.presentation === 'sheet') return;
    const overlayRef = entry.overlayRef;
    if (!overlayRef) return;
    const view = this.document.defaultView;
    if (!view) return;
    view.requestAnimationFrame(() => {
      const hostElement = entry.hostComponentRef?.location.nativeElement as HTMLElement | undefined;
      if (!entry.overlayRef || !hostElement) return;
      const rect = hostElement.getBoundingClientRect();
      if (rect.width === 0 && rect.height === 0) return;
      const viewport = this.readViewportSize();
      let x = rect.left;
      let y = rect.top;
      let changed = false;
      const maxY = viewport.height - rect.height - VIEWPORT_BOTTOM_MARGIN_PX;
      if (y > maxY) {
        y = Math.max(VIEWPORT_MARGIN_PX, maxY);
        changed = true;
      }
      const maxX = viewport.width - rect.width - VIEWPORT_MARGIN_PX;
      if (x > maxX) {
        x = Math.max(VIEWPORT_MARGIN_PX, maxX);
        changed = true;
      }
      if (!changed) return;
      // Flatten de huidige (mogelijk gesleepte) positie in de overlay-strategy
      // en reset de drag-translate zodat de geklemde top/left exact toegepast
      // wordt zonder de translate er nog eens bovenop.
      entry.hostComponentRef?.instance.resetDragPosition();
      const strategy = this.overlay.position().global().left(`${x}px`).top(`${y}px`);
      entry.overlayRef.updatePositionStrategy(strategy);
      entry.startPosition = { x, y };
      entry.lastFloatingPosition = { x, y };
    });
  }

  /**
   * Leest de huidige linkerboven-positie van de panel-host (inclusief een
   * eventuele drag-translate). `null` wanneer gedockt of geen (zichtbaar)
   * DOM-element beschikbaar is.
   */
  private readEntryPosition<TData>(
    entry: InternalFloatingPanelEntry<TData>,
  ): { x: number; y: number } | null {
    if (entry.state() !== 'floating') return null;
    const hostElement = entry.hostComponentRef?.location.nativeElement as HTMLElement | undefined;
    if (!hostElement) return null;
    const rect = hostElement.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) return null;
    return { x: rect.left, y: rect.top };
  }

  /**
   * Verplaatst het floating paneel naar een expliciete positie en reset de
   * drag-translate zodat de nieuwe top/left exact toegepast wordt. Bedoeld om
   * een eerder bewaarde plek te herstellen (bv. terug naar de kanaal-positie
   * na een tijdelijke poort-nudge). No-op wanneer gedockt of zonder overlay.
   */
  private moveEntryTo<TData>(
    entry: InternalFloatingPanelEntry<TData>,
    position: { x: number; y: number },
  ): void {
    if (entry.state() !== 'floating') return;
    // Sheets liggen vast onderaan de viewport — herpositioneren is een no-op.
    if (entry.presentation === 'sheet') return;
    const overlayRef = entry.overlayRef;
    if (!overlayRef) return;
    entry.hostComponentRef?.instance.resetDragPosition();
    const strategy = this.overlay.position().global().left(`${position.x}px`).top(`${position.y}px`);
    overlayRef.updatePositionStrategy(strategy);
    entry.startPosition = { ...position };
    entry.lastFloatingPosition = { ...position };
  }

  /** Backdrop én dialoog-laag van een net geopende dialoog bovenaan de gedeelde z-index-teller. */
  private raiseDialogAbovePanels(dialogRef: MatDialogRef<unknown>): void {
    const marker = `floating-panel-raised-dialog-${++this.raisedDialogCount}`;
    dialogRef.addPanelClass(marker);
    const wrapper = this.document.querySelector<HTMLElement>(`.${marker}`)?.parentElement;
    if (!wrapper) return;
    const backdrop = wrapper.previousElementSibling;
    if (backdrop instanceof HTMLElement && backdrop.classList.contains('cdk-overlay-backdrop')) {
      this.nextZIndex += 1;
      backdrop.style.zIndex = String(this.nextZIndex);
    }
    this.nextZIndex += 1;
    wrapper.style.zIndex = String(this.nextZIndex);
  }

  private bringToFront<TData>(entry: InternalFloatingPanelEntry<TData>): void {
    if (!entry.overlayRef) return;
    const overlayElement = entry.overlayRef.hostElement;
    if (overlayElement) {
      this.nextZIndex += 1;
      overlayElement.style.zIndex = String(this.nextZIndex);
    }
    this.markAsMostRecent(entry);
  }

  /**
   * Geeft het paneel een persistente coral border om aan te duiden dat dit
   * het meest recent geopende/gefocuste dialog is. Alle andere panel-hosts
   * verliezen de markering — er kan altijd maar één "meest recente" zijn.
   * Wordt opnieuw geëvalueerd bij elke `bringToFront`, en gewist wanneer het
   * paneel gedockt of gesloten wordt. De klasse zit op de FloatingPanelHost
   * (niet op het pane-element) zodat de bestaande pane-animaties
   * (enter/exit/dock) onaangeroerd blijven en niet opnieuw afspelen.
   */
  private markAsMostRecent<TData>(entry: InternalFloatingPanelEntry<TData>): void {
    const targetHost = entry.hostComponentRef?.location.nativeElement as HTMLElement | undefined;
    for (const other of this.entries()) {
      const otherHost = other.hostComponentRef?.location.nativeElement as HTMLElement | undefined;
      if (otherHost && otherHost !== targetHost) {
        otherHost.classList.remove('is-most-recent');
      }
    }
    targetHost?.classList.add('is-most-recent');
  }

  private clearMostRecent<TData>(entry: InternalFloatingPanelEntry<TData>): void {
    const hostElement = entry.hostComponentRef?.location.nativeElement as HTMLElement | undefined;
    hostElement?.classList.remove('is-most-recent');
  }

  private dockEntry<TData>(entry: InternalFloatingPanelEntry<TData>): void {
    if (entry.state() !== 'floating') return;
    // Sheets (phone) docken nooit — de dock-zijbalk bestaat daar niet.
    if (entry.presentation === 'sheet') return;
    if (!this.dockTargetAvailable()) return;
    this.captureFloatingPosition(entry);
    this.removeFromInteractedStack(entry.key);
    this.clearMostRecent(entry);
    this.detachOverlayWithExitAnimation(entry, 'is-docking');
    entry.state.set('docked');
  }

  private undockEntry<TData>(
    entry: InternalFloatingPanelEntry<TData>,
    at?: { x: number; y: number },
  ): void {
    if (entry.state() !== 'docked') return;
    const firstUndock = at == null && entry.lastFloatingPosition == null;
    entry.state.set('floating');
    entry.startPosition = at ?? entry.lastFloatingPosition ?? 'center';
    this.attachToOverlay(entry);
    if (firstUndock && entry.undockPlacement === 'beside-dock' && entry.presentation === 'floating') {
      this.placeBesideDockAfterRender(entry);
    }
    this.markInteracted(entry.key);
  }

  /**
   * Eerste undock met `undockPlacement: 'beside-dock'`: het paneel opende
   * gecentreerd; ná de render schuift het naar links tot zijn rechterrand
   * {@link BESIDE_DOCK_GAP_PX} vóór de zichtbare dock-rand staat, met behoud
   * van de verticale centrering. Staat het gecentreerd al links genoeg, dan
   * blijft het staan; links nooit voorbij de viewport-marge. De rAF valt vóór
   * de eerste paint: het paneel springt niet zichtbaar opzij.
   *
   * BELANGRIJK: meten met layoutmaten (`offsetWidth`/`offsetLeft`/`offsetTop`
   * van de pane), niet met `getBoundingClientRect`: de intro-animatie schaalt
   * het paneel op dat moment nog (±0,97), waardoor het ±30 px te smal gemeten
   * werd en in het rechterpaneel eindigde (Stevens FHD-test 2026-10-01).
   * Zie floating-panel.service.spec.ts "losmaken naast de dock".
   */
  private placeBesideDockAfterRender<TData>(entry: InternalFloatingPanelEntry<TData>): void {
    const view = this.document.defaultView;
    if (!view) return;
    view.requestAnimationFrame(() => {
      const pane = entry.overlayRef?.overlayElement;
      if (!entry.overlayRef || !pane || entry.state() !== 'floating') return;
      const width = pane.offsetWidth;
      const bounds = this.readDockBoundsRect();
      if (bounds == null || width === 0) return;
      const x = Math.max(VIEWPORT_MARGIN_PX, bounds.left - BESIDE_DOCK_GAP_PX - width);
      if (x >= pane.offsetLeft) return;
      const position = { x, y: pane.offsetTop };
      const strategy = this.overlay.position().global().left(`${position.x}px`).top(`${position.y}px`);
      entry.overlayRef.updatePositionStrategy(strategy);
      entry.startPosition = { ...position };
      entry.lastFloatingPosition = { ...position };
    });
  }

  /**
   * Zichtbare rand van de dock: de dichtstbijzijnde voorouder met
   * `data-dock-bounds` (bv. de kaart van het rechterpaneel rond de dock-area,
   * die zelf binnen de kaart-padding begint), anders het dock-element zelf.
   */
  private readDockBoundsRect(): DOMRect | null {
    const element = this.dockTargetElement();
    if (!element) return null;
    const bounds = element.closest<HTMLElement>('[data-dock-bounds]') ?? element;
    const rect = bounds.getBoundingClientRect();
    return rect.width === 0 && rect.height === 0 ? null : rect;
  }

  /**
   * Leest de huidige viewport-positie van de panel-host (incl. een eventuele
   * `CdkDrag`-translate) en bewaart die op de entry zodat een later undock de
   * oorspronkelijke plek kan herstellen. Wanneer er geen DOM-element (meer)
   * beschikbaar is wordt de bestaande waarde behouden.
   */
  private captureFloatingPosition<TData>(entry: InternalFloatingPanelEntry<TData>): void {
    const hostElement = entry.hostComponentRef?.location.nativeElement as HTMLElement | undefined;
    if (!hostElement) return;
    const rect = hostElement.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) return;
    entry.lastFloatingPosition = { x: rect.left, y: rect.top };
  }

  private closeEntry<TData>(entry: InternalFloatingPanelEntry<TData>): void {
    this.removeFromInteractedStack(entry.key);
    this.clearMostRecent(entry);
    this.entries.update(list => list.filter(otherEntry => otherEntry.key !== entry.key));
    this.detachOverlayWithExitAnimation(entry, 'is-closing');
    entry.state.set('closed');
  }

  private detachOverlay<TData>(entry: InternalFloatingPanelEntry<TData>): void {
    entry.hostResizeObserver?.disconnect();
    entry.hostResizeObserver = null;
    entry.overlayRef?.dispose();
    entry.overlayRef = null;
    entry.hostComponentRef = null;
  }

  /**
   * Detacht een floating panel met een korte exit-animatie zodat hij niet hard
   * verdwijnt. De pane krijgt eerst de meegegeven exit-klasse (zie SCSS:
   * `floating-panel-exit` voor `is-closing` en `floating-panel-dock` voor
   * `is-docking`) en pas wanneer de animatie klaar is wordt de overlay echt
   * opgekuist. Bij `prefers-reduced-motion` of wanneer er geen overlay (meer)
   * is wordt onmiddellijk dispose gedaan.
   *
   * De dock-variant verschuift ook lichtjes naar rechts richting de docked
   * tab-strip — UX-hint dat het paneel daarheen migreert eerder dan dat het
   * verdwijnt.
   */
  private detachOverlayWithExitAnimation<TData>(
    entry: InternalFloatingPanelEntry<TData>,
    exitClass: 'is-closing' | 'is-docking',
  ): void {
    const overlayRef = entry.overlayRef;
    if (!overlayRef) {
      this.detachOverlay(entry);
      return;
    }

    const prefersReducedMotion = this.document.defaultView?.matchMedia?.(
      '(prefers-reduced-motion: reduce)',
    ).matches ?? false;

    if (prefersReducedMotion) {
      this.detachOverlay(entry);
      return;
    }

    try {
      overlayRef.addPanelClass(exitClass);
    } catch {
      this.detachOverlay(entry);
      return;
    }

    entry.hostResizeObserver?.disconnect();
    entry.hostResizeObserver = null;
    entry.overlayRef = null;
    entry.hostComponentRef = null;

    const duration = exitClass === 'is-docking' ? 200 : 160;
    setTimeout(() => {
      overlayRef.dispose();
    }, duration);
  }

  private markInteracted(key: string): void {
    this.removeFromInteractedStack(key);
    this.lastInteracted.push(key);
  }

  private removeFromInteractedStack(key: string): void {
    const index = this.lastInteracted.indexOf(key);
    if (index >= 0) {
      this.lastInteracted.splice(index, 1);
    }
  }

  /**
   * Vertaalt een `FloatingPanelStartPosition` (center / vaste pixels /
   * smart-hint) naar een concrete `{ x, y }` of `null` voor center. De
   * smart-variant gebruikt {@link resolveSmartPosition} om auto-flip,
   * viewport-clamp en collision-avoidance toe te passen op basis van het
   * preferred-punt, de placement-hint en reeds geopende floating panels
   * (zie `docs/dialog-placement.md`).
   */
  private resolveStartPosition(
    startPosition: FloatingPanelStartPosition,
    existingRects: readonly DOMRect[],
    obstacles: readonly DOMRect[],
  ): { x: number; y: number } | null {
    if (startPosition === 'center') return null;
    if ('x' in startPosition && 'y' in startPosition) {
      return { x: startPosition.x, y: startPosition.y };
    }
    return this.resolveSmartPosition(
      startPosition as FloatingPanelSmartStartPosition,
      this.estimatePanelSize(existingRects),
      this.readViewportSize(),
      obstacles,
    );
  }

  /**
   * Voegt de dock-zone-rect (indien geregistreerd) toe aan een lijst
   * obstacles voor de placement-search. Hierdoor wordt het docking-paneel
   * behandeld als een vast "altijd aanwezig" paneel waar nieuwe floating
   * panels nooit overheen mogen landen — analoog aan §11.5 van de spec.
   */
  private withDockZone(existing: readonly DOMRect[]): readonly DOMRect[] {
    const dockRect = this.readDockZoneRect();
    if (!dockRect) return existing;
    return [...existing, dockRect];
  }

  /**
   * Leest de huidige bounding rect van het geregistreerde dock-element en
   * verruimt hem met {@link DOCK_ZONE_BUFFER_PX} aan elke zijde zodat
   * panels niet pal tegen de rand van het docking-paneel komen. Geeft
   * `null` als er geen dock-element is.
   */
  private readDockZoneRect(): DOMRect | null {
    const element = this.dockTargetElement();
    if (!element) return null;
    const rect = element.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) return null;
    return new DOMRect(
      rect.left - DOCK_ZONE_BUFFER_PX,
      rect.top - DOCK_ZONE_BUFFER_PX,
      rect.width + DOCK_ZONE_BUFFER_PX * 2,
      rect.height + DOCK_ZONE_BUFFER_PX * 2,
    );
  }

  /**
   * Bouwt de min/max-grenzen waarbinnen een floating panel mag landen.
   * Vertrekt van de viewport (minus marge) en knipt vervolgens de
   * dock-zone weg aan de zijde waar die zich bevindt. Wanneer er geen
   * dock-zone is, of de zone strekt zich uit over de volle viewport-hoogte
   * **niet** aan een rand → laten we de bounds ongewijzigd. Voor de
   * huidige chart-editor staat de dock-zone full-height tegen de
   * rechterkant — we knippen dus `maxX`.
   */
  private computePlacementBounds(
    panelSize: { readonly width: number; readonly height: number },
  ): { minX: number; maxX: number; minY: number; maxY: number } {
    const viewport = this.readViewportSize();
    let minX = VIEWPORT_MARGIN_PX;
    let maxX = Math.max(minX, viewport.width - panelSize.width - VIEWPORT_MARGIN_PX);
    const minY = VIEWPORT_MARGIN_PX;
    const maxY = Math.max(minY, viewport.height - panelSize.height - VIEWPORT_BOTTOM_MARGIN_PX);

    const dock = this.readDockZoneRect();
    if (dock) {
      // Heuristiek: enkel knippen wanneer de dock-zone (bijna) full-height
      // tegen één van de zijranden zit. Anders laten we bounds met rust en
      // laat de overlap-check de dock-zone als obstacle behandelen.
      const coversFullHeight = dock.top <= viewport.height * 0.2
        && dock.bottom >= viewport.height * 0.8;
      if (coversFullHeight) {
        if (dock.right >= viewport.width - VIEWPORT_MARGIN_PX) {
          // Dock kleeft aan rechterrand → snijd rechts af.
          maxX = Math.min(maxX, dock.left - panelSize.width);
        } else if (dock.left <= VIEWPORT_MARGIN_PX) {
          // Dock kleeft aan linkerrand → snijd links af.
          minX = Math.max(minX, dock.right);
        }
      }
      maxX = Math.max(minX, maxX);
    }
    return { minX, maxX, minY, maxY };
  }

  /**
   * Schat de afmetingen van het nieuwe paneel. Indien er al floating panels
   * open zijn nemen we het maximum van hun bounding rects als referentie —
   * de gate-info-varianten zijn vandaag uniform van grootte, dus dat geeft
   * de meest accurate kandidaat-oppervlakte voor de overlap-berekening. Bij
   * de allereerste open (geen rects beschikbaar) vallen we terug op
   * {@link DEFAULT_PANEL_SIZE}. Wanneer er een grote variant bijkomt zal
   * deze heuristiek nog steeds een conservatieve (= eerder te groot dan
   * te klein) schatting geven zolang er minstens één paneel van die soort
   * open staat.
   */
  private estimatePanelSize(
    existingRects: readonly DOMRect[],
  ): { readonly width: number; readonly height: number } {
    if (existingRects.length === 0) return DEFAULT_PANEL_SIZE;
    let width = 0;
    let height = 0;
    for (const rect of existingRects) {
      if (rect.width > width) width = rect.width;
      if (rect.height > height) height = rect.height;
    }
    if (width <= 0 || height <= 0) return DEFAULT_PANEL_SIZE;
    return { width, height };
  }

  /**
   * Bepaalt de uiteindelijke `(x, y)` voor een smart-startpositie.
   *
   * Strategie C (cf. `docs/dialog-placement.md` §3 + §4):
   *  1. **Initiële kandidaat**: kies voorkeurszijde via de `hint`
   *     (`prefer-right-of-anchor` ⇒ rechts onder preferred,
   *      `prefer-left-of-anchor` ⇒ links onder). Bij overflow → flip; bij
   *     dubbele overflow → klem tegen de dichtstbijzijnde viewport-rand.
   *  2. **Collision-avoidance** (Fase 2 — spiral search): wanneer de
   *     initiële kandidaat te veel overlapt met reeds open floating panels
   *     ({@link OVERLAP_TOLERANCE_RATIO}), proberen we extra kandidaten op
   *     groeiende ringen rond het preferred-punt ({@link SEARCH_RADII_PX}).
   *     De richtingen worden gewogen volgens de `hint` zodat we eerst de
   *     gewenste zijde proberen. Eerste kandidaat onder de drempel wint;
   *     anders → kandidaat met de laagste overlap (graceful degradation).
   */
  private resolveSmartPosition(
    smart: FloatingPanelSmartStartPosition,
    panelSize: { readonly width: number; readonly height: number },
    viewport: { readonly width: number; readonly height: number },
    obstacles: readonly DOMRect[],
  ): { x: number; y: number } {
    const hint: FloatingPanelPlacementHint = smart.hint ?? 'prefer-right-of-anchor';
    const { preferred } = smart;
    const bounds = this.computePlacementBounds(panelSize);

    const initial = this.computeInitialCandidate(preferred, panelSize, viewport, hint, bounds);

    if (obstacles.length === 0) return initial;

    return this.findBestCandidate(initial, preferred, panelSize, obstacles, hint, bounds);
  }

  /**
   * Berekent de "ideale" landingspositie zonder rekening te houden met
   * andere panels: rechts/links onder het preferred-punt volgens hint, met
   * flip + viewport-clamp.
   */
  private computeInitialCandidate(
    preferred: { readonly x: number; readonly y: number },
    panelSize: { readonly width: number; readonly height: number },
    _viewport: { readonly width: number; readonly height: number },
    hint: FloatingPanelPlacementHint,
    bounds: { minX: number; maxX: number; minY: number; maxY: number },
  ): { x: number; y: number } {
    const xRight = preferred.x + PANEL_CURSOR_OFFSET_PX;
    const xLeft = preferred.x - panelSize.width - PANEL_CURSOR_OFFSET_PX;
    // Honoreer dock-zone via `bounds` i.p.v. de naakte viewport-randen.
    const rightFits = xRight <= bounds.maxX;
    const leftFits = xLeft >= bounds.minX;

    let x: number;
    if (hint === 'prefer-left-of-anchor') {
      if (leftFits) x = xLeft;
      else if (rightFits) x = xRight;
      else x = bounds.maxX;
    } else {
      if (rightFits) x = xRight;
      else if (leftFits) x = xLeft;
      else x = bounds.minX;
    }
    x = Math.min(Math.max(x, bounds.minX), bounds.maxX);

    const yDown = preferred.y + PANEL_CURSOR_OFFSET_PX;
    const y = Math.min(Math.max(yDown, bounds.minY), bounds.maxY);
    return { x, y };
  }

  /**
   * Spiral-search rond het preferred-punt: probeer kandidaten op groeiende
   * ringen ({@link SEARCH_RADII_PX}) in acht richtingen, geordend zodat de
   * hint-zijde eerst getest wordt. Per kandidaat wordt overlap met alle
   * bestaande panels berekend; we stoppen zodra een kandidaat onder
   * {@link OVERLAP_TOLERANCE_RATIO} ligt. Wanneer geen enkele kandidaat de
   * drempel haalt geven we de kandidaat met de laagste gemeten overlap
   * terug (graceful degradation).
   */
  private findBestCandidate(
    initial: { x: number; y: number },
    preferred: { readonly x: number; readonly y: number },
    panelSize: { readonly width: number; readonly height: number },
    existingRects: readonly DOMRect[],
    hint: FloatingPanelPlacementHint,
    bounds: { minX: number; maxX: number; minY: number; maxY: number },
  ): { x: number; y: number } {
    const initialOverlap = this.maxOverlapRatio(initial, panelSize, existingRects);
    if (initialOverlap < OVERLAP_TOLERANCE_RATIO) return initial;

    let bestPos = initial;
    let bestOverlap = initialOverlap;
    const seen = new Set<string>();
    seen.add(this.keyForPos(initial));

    const directions = this.directionsForHint(hint);

    for (const radius of SEARCH_RADII_PX) {
      if (radius === 0) continue;
      for (const dir of directions) {
        const raw = {
          x: preferred.x + dir.dx * radius + PANEL_CURSOR_OFFSET_PX * Math.sign(dir.dx || 1),
          y: preferred.y + dir.dy * radius + PANEL_CURSOR_OFFSET_PX,
        };
        // Voor links-gerichte kandidaten: leg de rechter-rand bij preferred
        // i.p.v. de linker-rand, anders staat het paneel verkeerd om.
        if (dir.dx < 0) {
          raw.x = preferred.x - panelSize.width - PANEL_CURSOR_OFFSET_PX + (dir.dx + 1) * radius;
        }
        const candidate = {
          x: Math.min(Math.max(raw.x, bounds.minX), bounds.maxX),
          y: Math.min(Math.max(raw.y, bounds.minY), bounds.maxY),
        };
        const key = this.keyForPos(candidate);
        if (seen.has(key)) continue;
        seen.add(key);

        const overlap = this.maxOverlapRatio(candidate, panelSize, existingRects);
        if (overlap < OVERLAP_TOLERANCE_RATIO) return candidate;
        if (overlap < bestOverlap) {
          bestOverlap = overlap;
          bestPos = candidate;
        }
      }
    }
    return bestPos;
  }

  /**
   * Acht-richtingen lijst (eenheidsvectoren) waarbij de zijde die overeenkomt
   * met de placement-hint vooraan staat. Voor `prefer-right-of-anchor`
   * proberen we eerst rechts/rechtsonder/rechtsboven, dan beneden/boven,
   * dan pas links — en omgekeerd voor `prefer-left-of-anchor`. Zo komt het
   * paneel zo dicht mogelijk bij de gewenste zijde van het anker, ook bij
   * collision.
   */
  private directionsForHint(
    hint: FloatingPanelPlacementHint,
  ): readonly { readonly dx: number; readonly dy: number }[] {
    const right = { dx: 1, dy: 0 };
    const rightDown = { dx: 1, dy: 1 };
    const rightUp = { dx: 1, dy: -1 };
    const down = { dx: 0, dy: 1 };
    const up = { dx: 0, dy: -1 };
    const left = { dx: -1, dy: 0 };
    const leftDown = { dx: -1, dy: 1 };
    const leftUp = { dx: -1, dy: -1 };
    if (hint === 'prefer-left-of-anchor') {
      return [left, leftDown, leftUp, down, up, right, rightDown, rightUp];
    }
    return [right, rightDown, rightUp, down, up, left, leftDown, leftUp];
  }

  private keyForPos(pos: { x: number; y: number }): string {
    return `${Math.round(pos.x)},${Math.round(pos.y)}`;
  }

  /**
   * Berekent de hoogste overlap-ratio tussen de kandidaat-rect (top-left
   * `pos`, gegeven `panelSize`) en elk van de meegegeven bestaande rects.
   * Ratio = overlap-oppervlakte / kandidaat-oppervlakte. 0 = geen overlap,
   * 1 = volledig bedekt.
   */
  private maxOverlapRatio(
    pos: { x: number; y: number },
    panelSize: { readonly width: number; readonly height: number },
    existingRects: readonly DOMRect[],
  ): number {
    const candidateArea = panelSize.width * panelSize.height;
    if (candidateArea <= 0) return 0;
    const candidateRight = pos.x + panelSize.width;
    const candidateBottom = pos.y + panelSize.height;

    let maxRatio = 0;
    for (const rect of existingRects) {
      const overlapX = Math.max(0, Math.min(candidateRight, rect.right) - Math.max(pos.x, rect.left));
      if (overlapX <= 0) continue;
      const overlapY = Math.max(0, Math.min(candidateBottom, rect.bottom) - Math.max(pos.y, rect.top));
      if (overlapY <= 0) continue;
      const ratio = (overlapX * overlapY) / candidateArea;
      if (ratio > maxRatio) maxRatio = ratio;
      if (maxRatio >= 1) return 1;
    }
    return maxRatio;
  }

  /**
   * Verzamelt de huidige bounding rects van alle floating panels (exclusief
   * de meegegeven `excludeKey`). Wordt gebruikt door de collision-avoidance
   * loop om overlap met reeds open panels te vermijden. Panels zonder
   * gemounte host-ref of met null-rect worden overgeslagen.
   */
  private collectFloatingPanelRects(excludeKey?: string): readonly DOMRect[] {
    const rects: DOMRect[] = [];
    for (const entry of this.entries()) {
      if (entry.key === excludeKey) continue;
      if (entry.state() !== 'floating') continue;
      const element = entry.hostComponentRef?.location.nativeElement as HTMLElement | undefined;
      if (!element) continue;
      const rect = element.getBoundingClientRect();
      if (rect.width === 0 && rect.height === 0) continue;
      rects.push(rect);
    }
    return rects;
  }

  private readViewportSize(): { width: number; height: number } {
    const view = this.document.defaultView;
    return {
      width: view?.innerWidth ?? this.document.documentElement.clientWidth,
      height: view?.innerHeight ?? this.document.documentElement.clientHeight,
    };
  }

  private ensureEscListener(): void {
    if (this.escListenerAttached) return;
    this.escListener = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      const topKey = this.lastInteracted[this.lastInteracted.length - 1];
      if (!topKey) return;
      const entry = this.entries().find(panelEntry => panelEntry.key === topKey);
      if (entry && entry.state() === 'floating') {
        entry.publicRef.close();
      }
    };
    this.document.addEventListener('keydown', this.escListener);
    this.escListenerAttached = true;
  }
}
